// src/context/CartContext.jsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  cartGet,
  cartAdd,
  cartUpdateItem,
  cartRemoveItem,
  cartClear,
} from "../storeAPIs/StoreCartApis";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isLoggedIn, isLoading: authLoading } = useAuth();

  const [cart, setCart] = useState({ items: [], total: 0, count: 0 });
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null); // { id, name, quantity }

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setCart({ items: [], total: 0, count: 0 });
      setReady(true);
      return;
    }
    setLoading(true);
    try {
      const res = await cartGet();
      setCart(res.data);
    } catch (err) {
      
      setCart({ items: [], total: 0, count: 0 });
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (authLoading) return;
    setReady(false);
    refresh();
  }, [authLoading, refresh]);

  const addItem = async (product, qty = 1) => {
    if (!isLoggedIn) throw new Error("NOT_LOGGED_IN");
    const res = await cartAdd(product.id, qty);
    setCart(res.data);
    setLastAdded({
      id: product.id,
      name: product.name,
      quantity: qty,
    });
    setDrawerOpen(true);
    return res.data;
  };

  const updateQty = async (itemId, qty) => {
    const res = await cartUpdateItem(itemId, qty);
    setCart(res.data);
  };

  const removeItem = async (itemId) => {
    const res = await cartRemoveItem(itemId);
    setCart(res.data);
  };

  const clear = async () => {
    await cartClear();
    setCart({ items: [], total: 0, count: 0 });
  };

  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  const toggleDrawer = () => setDrawerOpen((v) => !v);

  const value = {
    items: cart.items ?? [],
    total: Number(cart.total) || 0,
    count: cart.count || 0,
    ready,
    loading,
    isLoggedIn,
    addItem,
    updateQty,
    removeItem,
    clear,
    refresh,
    drawerOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    lastAdded,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}